/* @flow */
import * as React from "react";
import {
  withRouter,
  BrowserRouter as Router,
  Route,
  Switch,
  Redirect
} from "react-router-dom";
import idx from "idx";
import { connect, Provider } from "react-redux";
import createStore from "./redux/createStore";
import { getErrorMessage } from "./redux/reducer/serverFaults";
import withData from "./components/withData";
import withLanguage from "./components/withLocaleChange";
import ErrorPopup from "./components/ErrorPopup/Error";
import FailureScreen from "./components/FailureScreen";
import ReferralBonusModal from "./components/TradeModals/ReferralBonus";
import {
  DictionaryUpdater,
  inferLocale,
  withGettext
} from "./components/common/Gettext";
import { Helmet } from "react-helmet";
import Subzero from "./components/Subzero";
import DataPlease, { ProfilePlease } from "./components/DataPlease";
import ReconnectableSocket from "./components/ReconnectableSocket";
import Loadable from "./components/Loadable";
import dataLayer from "./components/common/dataLayer";
import { mapDispatchToFetch } from "./components/common/fetch";
import FirebaseCloudMessaging from "./components/FirebaseCloudMessaging";
import RouteConfig from "./routes";
import BrandSpinner from "./components/Spinners/BrandSpinner";
import FeatureFlags from "./FeatureFlags";
import Audio from "./components/Audio";
import ThemeProvider, {
  TradeThemeContextProvider
} from "./components/ThemeProvider";
import RtlProvider from "./components/RtlProvider";
import AddToHomeScreen from "./components/AddToHomeScreen";
import { TournamentAPIProvider } from "./components/Tournaments/Api";
import { TournamentIdProvider } from "./components/Tournaments/TournamentId";
import { ModalProvider, ModalRoot } from "./components/common/ModalProvider";
import { ModalRootProvider } from "./components/common/Modal";
import AddToHomescreenPopup from "./components/AddToHomescreenPopup";
import OnLine from "./components/OnLine";
import withCrashLog from "./components/common/crashLog";
import { Rate } from "./components/webview/Rate";
import ABTest from "./components/ABTest";
import { clearSnapshot } from "./components/Chart/ChartEventHandlers";
import { submitAppsFlyerParameters } from "./components/Scorpion";
import TournamentInvite from "./components/TournamentInvite";

function fetchDictionary(language) {
  switch (language) {
    case "en":
      // MomentJS uses EN locale as default so no need to load it
      return import(/* webpackChunkName: "en" */ "./__dictionaries/en.json");
    case "vn":
    case "vi":
      return Promise.all([
        import("moment/locale/vi"),
        import(/* webpackChunkName: "vn" */ "./__dictionaries/vn.json")
      ]).then(([a, b]) => b);
    case "ru":
      return Promise.all([
        import("moment/locale/ru"),
        import(/* webpackChunkName: "ru" */ "./__dictionaries/ru.json")
      ]).then(([a, b]) => b);
    case "jp":
      return Promise.all([
        import("moment/locale/jv"),
        import(/* webpackChunkName: "jp" */ "./__dictionaries/jp.json")
      ]).then(([a, b]) => b);
    case "ph":
    case "tl":
      return Promise.all([
        import("moment/locale/tl-ph"),
        import(/* webpackChunkName: "ph" */ "./__dictionaries/ph.json")
      ]).then(([a, b]) => b);
    case "th":
      return Promise.all([
        import("moment/locale/th"),
        import(/* webpackChunkName: "th" */ "./__dictionaries/th.json")
      ]).then(([a, b]) => b);
    case "id":
      return Promise.all([
        import("moment/locale/id"),
        import(/* webpackChunkName: "id" */ "./__dictionaries/id.json")
      ]).then(([a, b]) => b);
    case "hi":
      return Promise.all([
        import("moment/locale/hi"),
        import(/* webpackChunkName: "hi" */ "./__dictionaries/hi.json")
      ]).then(([a, b]) => b);
    case "ar":
      return Promise.all([
        import("moment/locale/ar"),
        import(/* webpackChunkName: "ar" */ "./__dictionaries/ar.json")
      ]).then(([a, b]) => b);
    case "es":
      return Promise.all([
        import("moment/locale/es"),
        import(/* webpackChunkName: "es" */ "./__dictionaries/es.json")
      ]).then(([a, b]) => b);
    case "cn":
      return Promise.all([
        import("moment/locale/zh-cn"),
        import(/* webpackChunkName: "cn" */ "./__dictionaries/cn.json")
      ]).then(([a, b]) => b);
    case "tr":
      return Promise.all([
        import("moment/locale/tr"),
        import(/* webpackChunkName: "tr" */ "./__dictionaries/tr.json")
      ]).then(([a, b]) => b);
    default:
      return import(/* webpackChunkName: "en" */ "./__dictionaries/en.json");
  }
}

const Error404 = Loadable(() =>
  import(/* webpackChunkName: "404" */ "./pages/404"),
  "404"
);

export const store = createStore();

class ChangeLocalePlease extends React.Component {
  sent = false;

  render() {
    if (!this.props.profile || this.sent) {
      return null;
    }

    const langInURL = /lang=([a-zA-Z]{2})/.exec(window.location.search);
    if (langInURL) {
      const lang = langInURL[1];
      this.props.onLocaleChange(lang);
      this.sent = true;
    }

    return null;
  }
}

const ChangeLocalePleaseWhenReady = withData({ optional: ["profile"] })(
  withLanguage(ChangeLocalePlease)
);

class _App extends React.Component<$FlowFixMeProps, { isAlive: boolean }> {
  state = {
    isOnline: true,
    isAlive: true,
    errors: null
  };

  onApiProviderLogout = () => {
    if (this.props.profile != null) {
      this.props.dispatch({ type: "INVALIDATE_PROFILE" });
    }
  };

  UNSAFE_componentWillMount() {
    //for compatibility with the Android app

    if (process.env.REACT_APP_DEPLOY_TARGET === "siamoption" && typeof window.siamoption !== "undefined") {
      window.androidApp = window.siamoption;
    }
    this.unsubscribeFromHistory = this.props.history.listen(
      (location, action) => {
        // if not helpdesk
        if (!/\bhelpdesk\b/.test(location.pathname)) {
          // scroll to top on page change
          window.scrollTo(0, 0);
        }

        this.props.dispatch({ type: "DISPLAY_AUTH_TAB", payload: "" });
      }
    );

    if (process.env.REACT_APP_DEPLOY_TARGET === "irontrade") {
      // Костыль с таймзоной для бэкенда только для айрона
      const tz = new Date().getTimezoneOffset() / 60;
      this.props.fetch("/ajax/setTimezone?tz=" + tz);
    }
  }

  componentDidMount() {
    submitAppsFlyerParameters();
  }

  componentWillUnmount() {
    if (typeof this.unsubscribeFromHistory === "function") {
      this.unsubscribeFromHistory();
    }
  }

  UNSAFE_componentWillUpdate(prevState) {
    if (prevState.location.pathname !== this.props.location.pathname) {
      this.props.dispatch({ type: "DISPLAY_AUTH_FORM", payload: false });
      clearSnapshot();
    }
  }

  componentDidCatch(error, info) {
    this.setState({
      isAlive: false,
      errors: `${error.message}\n${error.stack}\n${info.componentStack}`
    });

    this.props.crashLog({
      error: {
        message: `${error.name} ${error.message}`,
        info: {
          url: document.location.href,
          stack: error.stack
        }
      }
    });

    dataLayer({
      event: "exception-to-ga",
      exDescription: error.message + info.componentStack,
      exFatal: true
    });
  }

  render() {
    if (!this.state.isAlive) {
      return <FailureScreen errors={this.state.errors} />;
    }

    if (this.props.profileStatus === "PENDING" && this.props.profile == null) {
      return (
        <React.Fragment>
          <BrandSpinner />
          <ProfilePlease />
        </React.Fragment>
      );
    }

    const sideEffects = (
      <React.Fragment key="sideEffects">
        <OnLine onChange={isOnline => this.setState({ isOnline })} />
        {this.props.soundsEnabled && <Audio />}
        <DictionaryUpdater fetchDictionary={fetchDictionary} />
        <ChangeLocalePleaseWhenReady />
        <DataPlease />
        <ProfilePlease />
        <Helmet>
          {process.env.REACT_APP_DEPLOY_TARGET === "irontrade" && (
            <title>
              {this.props
                .gettext(["Trading with Irontrade.com, safe and easy start."])}
            </title>
          )}
          {process.env.REACT_APP_DEPLOY_TARGET === "irontrade" && (
            <meta
              name="description"
              content={this.props
                .gettext(["Trade with one of the most rapidly growing trading platforms. Easy withdrawals, stable quotations, low spreads, and excellent support service with Irontrade."])}
            />
          )}
          {process.env.REACT_APP_DEPLOY_TARGET === "irontrade" ? (
            <meta
              name="google-site-verification"
              content="7NBEJHU048g1GKn9SCunSEVaeqIPa83T5oEQZciUAV0"
            />
          ) : (
            <meta
              name="google-site-verification"
              content="qZV-8wLeVxlWTpZHPG7qupDj_CKSUn37e1pDZXgSGn8"
            />
          )}
        </Helmet>
        <ReconnectableSocket pass={!this.props.profile || !this.props.profile.active_account_id} />
        <Subzero />
        <AddToHomeScreen isMobile={this.props.isMobile} />
      </React.Fragment>
    );

    let visuals = (
      <React.Fragment>
        {process.env.REACT_APP_DEPLOY_TARGET === "irontrade" && <AddToHomescreenPopup
          key={this.props.location.pathname}
          sessionCount={
            (this.props.profile && this.props.profile.session_count) || null
          }
        />}
        {FeatureFlags.enableOneSignal && <FirebaseCloudMessaging />}
        <Switch>
          {process.env.REACT_APP_PROJECT === "app" && (
            <Route exact path="/" render={() => <Redirect to="/trade" />} />
          )}

          {RouteConfig.map(route => (
            <Route key={route.path} {...route} />
          ))}
          <Route component={Error404} />
        </Switch>
        {this.props.errorMessage && (
          <ErrorPopup
            onDismiss={this.props.dismissErrorMessage}
            message={this.props.errorMessage}
          />
        )}
        {!this.state.isOnline && (
          <ErrorPopup
            onDismiss={() => this.setState({ isOnline: true })}
            message={this.props.gettext`Internet connection failed!`}
          />
        )}
        <TournamentInvite />
      </React.Fragment>
    );

    let referralBonus = this.props.referralBonus && (
      <ReferralBonusModal
        {...this.props.referralBonus}
        onDismiss={this.props.dismissReferralBonusMessage}
      />
    );

    const id = idx(this.props, props => props.profile.id) || "";
    const ua = window.navigator.userAgent.toLowerCase();
    // Android Webview full app
    const isWebView = /wvbo_full/g.test(ua);
    // Android Webview analytics
    const isAnalyticsWebView =
      process.env.REACT_APP_DEPLOY_TARGET === "irontrade" && /androidweb.*com\.irontrade\.analytics.*wvbo/g.test(ua) && !isWebView;
    const showRate =
      (isWebView || isAnalyticsWebView) &&
      window.nativeInterface &&
      window.nativeInterface.redirectToMarket;

    return (
      <ModalRootProvider>
        <ModalProvider>
          <TournamentIdProvider>
            <TournamentAPIProvider
              onLogout={this.onApiProviderLogout}
              key={id}
              profile={this.props.profile}
              locale={this.props.locale}
              currency={this.props.currency}
            >
              <React.Fragment>
                <ModalRoot />
                {process.env.REACT_APP_DEPLOY_TARGET === "irontrade" && showRate && (
                  <ABTest campaign="rating_from_push">
                    <Rate />
                  </ABTest>
                )}
                {sideEffects}
                {visuals}
                {referralBonus}
              </React.Fragment>
            </TournamentAPIProvider>
          </TournamentIdProvider>
        </ModalProvider>
      </ModalRootProvider>
    );
  }
}

function mapStateToProps(state) {
  const currency = state.serverData
    ? state.serverData.currency
    : (state.apiConfigs && state.apiConfigs.defaultCurrency) || "USD";

  return {
    soundsEnabled: state.audio.enabled,
    isMobile: state.serverData && state.serverData.isMobile,
    projectTitle: state.serverData && state.serverData.projectTitle,
    supportEmail: state.serverData && state.serverData.supportEmail,
    errorMessage: getErrorMessage(state.serverFaults),
    referralBonus: state.referralBonus,
    profile: state.profile,
    locale: inferLocale(state),
    currency,
    profileStatus: state.dataRequirementsStatus.profile
  };
}

function mapDispatchToProps(dispatch) {
  return {
    dismissErrorMessage() {
      dispatch({ type: "DISMISS_SERVER_FAULT" });
    },
    dismissReferralBonusMessage() {
      dispatch({ type: "DISMISS_REFERRAL_BONUS" });
    },
    ...mapDispatchToFetch(dispatch)
  };
}

const App = withGettext(
  withCrashLog(
  withRouter(
    connect(
      mapStateToProps,
      mapDispatchToProps
    )(_App)
  )
));

export default function TopLevelShit() {
  return (
    <Provider store={store}>
      <ThemeProvider>
        <RtlProvider>
          <TradeThemeContextProvider>
            <Router>
              <App />
            </Router>
          </TradeThemeContextProvider>
        </RtlProvider>
      </ThemeProvider>
    </Provider>
  );
}



// WEBPACK FOOTER //
// ./src/App.js